import React, { useEffect, useRef, useState } from 'react';
import { MessageCircle, X, Minus, Send, Search, ArrowLeft, Maximize2, Minimize2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useChatContext, type ChatConversation, type Message } from '@/contexts/ChatContext';
import { useIsBlockedPair } from '@/hooks/hub/useHubBlocks';
import { useAuth } from '@/contexts/AuthContext';
import { useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { UserAvatar } from '@/components/common/UserAvatar';

const MESSENGER_TOGGLE_ATTR = 'data-messenger-toggle';

const MessengerDock: React.FC = () => {
  const { user } = useAuth();
  const location = useLocation();
  const {
    conversations,
    hiddenConversations,
    openChats,
    messengerOpen,
    closeMessenger,
    openChatWithUser,
    closeChat,
  } = useChatContext();

  const [isMobile, setIsMobile] = useState(false);
  const [query, setQuery] = useState('');
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const isPublicPage = location.pathname === '/' || location.pathname === '/auth';
  const shouldRender = !!user && !isPublicPage;

  // Click outside closes the desktop popover
  useEffect(() => {
    if (!shouldRender || !messengerOpen || isMobile) return;
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node | null;
      if (!target) return;
      if (panelRef.current?.contains(target)) return;
      // Ignore clicks on the messenger toggle button (it handles its own toggle)
      const el = target as HTMLElement;
      if (el.closest?.(`[${MESSENGER_TOGGLE_ATTR}]`)) return;
      closeMessenger();
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [shouldRender, messengerOpen, isMobile, closeMessenger]);

  // Escape key always exits: close the newest open conversation, else the list.
  useEffect(() => {
    if (!shouldRender) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      const last = openChats[openChats.length - 1];
      if (last) closeChat(last);
      else if (messengerOpen) closeMessenger();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [shouldRender, openChats, messengerOpen, closeChat, closeMessenger]);

  // Device / browser back gesture exits the DM view instead of navigating away.
  useEffect(() => {
    if (!shouldRender) return;
    const hasOverlay = messengerOpen || openChats.length > 0;
    if (!hasOverlay) return;
    window.history.pushState({ lrDm: true }, '');
    const onPop = () => {
      const last = openChats[openChats.length - 1];
      if (last) closeChat(last);
      else closeMessenger();
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldRender, messengerOpen, openChats.length]);

  if (!shouldRender) return null;


  const visible = conversations.filter(c => !c.isAtlas && !hiddenConversations.includes(c.id));
  const searched = query
    ? visible.filter(c => (c.name || '').toLowerCase().includes(query.toLowerCase()))
    : visible;
  // Facebook-style: unread rises to the top, otherwise keep last_activity order.
  const filtered = [...searched].sort((a, b) => {
    const au = a.unreadCount > 0 ? 1 : 0;
    const bu = b.unreadCount > 0 ? 1 : 0;
    return bu - au;
  });

  const openConvs = openChats
    .map(id => visible.find(c => c.id === id))
    .filter((c): c is ChatConversation => !!c);

  const handleRowClick = (c: ChatConversation) => {
    if (c.userId) {
      openChatWithUser(c.userId, c.name, c.avatar ?? undefined, c.loadId);
    }
    // Facebook Messenger behavior: opening a chat always collapses the slider
    // so the pop-out window isn't blocked. Reopen the slider via the mascot.
    closeMessenger();
  };


  const PanelBody = (
    <div className="flex flex-col h-full">
      <div className="p-4 border-b border-border flex items-center justify-between gap-2">
        <div className="flex items-center gap-1 min-w-0">
          <button
            onClick={closeMessenger}
            aria-label="Back"
            className="-ml-2 flex h-11 w-11 items-center justify-center rounded-full hover:bg-muted active:bg-muted/70 flex-shrink-0"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h2 className="text-lg font-semibold truncate">Messages</h2>
        </div>
        <div className="flex items-center gap-2">
          {visible.length > 0 && (
            <span className="text-xs text-muted-foreground">{visible.length}</span>
          )}
          <button
            onClick={closeMessenger}
            aria-label="Close messages"
            className="hidden sm:inline-flex p-2 -mr-1 rounded-full hover:bg-muted active:bg-muted/70"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>


      <div className="p-3 border-b border-border">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search Messenger"
            className="w-full pl-9 pr-3 py-2 text-sm rounded-full bg-muted border-0 focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
      </div>
      <div className="flex-1 overflow-y-auto">
          {filtered.length === 0 ? (
            <div className="text-center text-muted-foreground min-h-full flex flex-col items-center justify-center px-6">
              <MessageCircle className="w-10 h-10 mx-auto mb-3 opacity-40" />
              <p className="text-sm font-medium">No conversations</p>
              <p className="text-xs mt-1">Direct messages will appear here.</p>
            </div>
          ) : (

          <ul>
            {filtered.map(c => (
              <li key={c.id}>
                <button
                  onClick={() => handleRowClick(c)}
                  className={cn(
                    'w-full flex items-center gap-3 px-3 py-2.5 hover:bg-muted transition-colors text-left',
                    c.unreadCount > 0 && 'bg-muted/40',
                  )}
                >
                  <div className="relative shrink-0">
                    <UserAvatar path={c.avatar} name={c.name} size="md" />
                    {c.isOnline && (
                      <span className="absolute bottom-0 right-0 h-3 w-3 rounded-full bg-emerald-500 ring-2 ring-background" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <p className={cn('text-sm truncate', c.unreadCount > 0 ? 'font-semibold' : 'font-medium')}>
                        {c.name}
                      </p>
                    </div>
                    <p className={cn('text-xs truncate', c.unreadCount > 0 ? 'text-foreground font-medium' : 'text-muted-foreground')}>
                      {c.lastMessage || 'No messages yet'}
                    </p>
                  </div>
                  {c.unreadCount > 0 && (
                    <span className="ml-2 shrink-0 min-w-[20px] h-5 px-1.5 rounded-full bg-primary text-primary-foreground text-[11px] font-bold flex items-center justify-center tabular-nums">
                      {c.unreadCount > 99 ? '99+' : c.unreadCount}
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );

  return (
    <>
      {/* Messages panel — full sheet on mobile, floating dropdown on desktop */}
      {isMobile ? (
        <Sheet open={messengerOpen} onOpenChange={(o) => { if (!o) closeMessenger(); }}>
          <SheetContent side="right" className="w-full sm:max-w-sm p-0 flex flex-col">
            {PanelBody}
          </SheetContent>
        </Sheet>
      ) : (
        messengerOpen && (
          <div
            ref={panelRef}
            role="dialog"
            aria-label="Messages"
            className="fixed left-2 right-2 sm:left-auto sm:right-4 top-16 z-50 w-auto sm:w-[360px] h-[calc(100vh-5rem)] bg-popover text-popover-foreground border border-border rounded-2xl shadow-2xl flex flex-col overflow-hidden animate-in fade-in-0 zoom-in-95 slide-in-from-top-2"

          >
            {PanelBody}
          </div>
        )
      )}


      {/* Pop-out chat windows — multiple at once, bottom-right stack (desktop),
          full-sheet on mobile. X closes → returns to slider. */}
      {isMobile ? (
        openConvs.map(conv => (
          <Sheet key={conv.id} open onOpenChange={() => closeChat(conv.id)}>
            <SheetContent
              side="bottom"
              hideCloseButton
              className="h-[100dvh] max-h-[100dvh] p-0 rounded-none flex flex-col overflow-hidden pt-[max(env(safe-area-inset-top),0.75rem)] pb-[env(safe-area-inset-bottom)]"
            >

              <ChatBody conversation={conv} onClose={() => closeChat(conv.id)} compact={false} />
            </SheetContent>
          </Sheet>
        ))
      ) : (
        openConvs.length > 0 && (
          <div className="fixed bottom-4 right-4 z-50 flex items-end gap-2">
            {openConvs.map(conv => (
              <ChatPopout key={conv.id} conversation={conv} onClose={() => closeChat(conv.id)} />
            ))}
          </div>
        )
      )}
    </>
  );
};

interface PopoutProps { conversation: ChatConversation; onClose: () => void; }

// Default (unchanged from original: w-80 / h-[26rem])
const DEFAULT_W = 320;
const DEFAULT_H = 416;
const EXPANDED_W = 520;
const EXPANDED_H = 640;
const MIN_W = 300;
const MIN_H = 320;

const maxW = () => Math.max(MIN_W, Math.min(900, window.innerWidth - 48));
const maxH = () => Math.max(MIN_H, Math.min(900, window.innerHeight - 48));

// Session-level (module scope) so the choice survives remounts without storage.
const popoutSizeSession: Record<string, { expanded: boolean; w?: number; h?: number }> = {};

const ChatPopout: React.FC<PopoutProps> = ({ conversation, onClose }) => {
  const [minimized, setMinimized] = useState(conversation.isMinimized || false);
  const { user } = useAuth();
  const { setConversations } = useChatContext();

  const saved = popoutSizeSession[conversation.id];
  const [expanded, setExpanded] = useState(saved?.expanded ?? false);
  const [custom, setCustom] = useState<{ w: number; h: number } | null>(
    saved?.w && saved?.h ? { w: saved.w, h: saved.h } : null,
  );

  useEffect(() => {
    popoutSizeSession[conversation.id] = { expanded, w: custom?.w, h: custom?.h };
  }, [conversation.id, expanded, custom]);

  const baseW = custom?.w ?? (expanded ? EXPANDED_W : DEFAULT_W);
  const baseH = custom?.h ?? (expanded ? EXPANDED_H : DEFAULT_H);
  const width = Math.min(baseW, maxW());
  const height = Math.min(baseH, maxH());

  const dragRef = useRef<{ x: number; y: number; w: number; h: number } | null>(null);
  const [dragging, setDragging] = useState(false);

  const onHandleDown = (e: React.PointerEvent) => {
    e.preventDefault();
    dragRef.current = { x: e.clientX, y: e.clientY, w: width, h: height };
    setDragging(true);
    const onMove = (ev: PointerEvent) => {
      const d = dragRef.current;
      if (!d) return;
      // Anchored bottom-right: dragging up/left grows the window.
      const w = Math.max(MIN_W, Math.min(maxW(), d.w + (d.x - ev.clientX)));
      const h = Math.max(MIN_H, Math.min(maxH(), d.h + (d.y - ev.clientY)));
      setCustom({ w, h });
    };
    const onUp = () => {
      dragRef.current = null;
      setDragging(false);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  useEffect(() => {
    if (minimized || !user || !conversation.id) return;
    if (conversation.unreadCount > 0) {
      (async () => {
        try {
          await supabase
            .from('messages')
            .update({ read_at: new Date().toISOString() })
            .eq('conversation_id', conversation.id)
            .neq('sender_id', user.id)
            .is('read_at', null);
          setConversations(prev => prev.map(c => c.id === conversation.id ? { ...c, unreadCount: 0 } : c));
        } catch (e) {
          console.error('mark read failed', e);
        }
      })();
    }
  }, [conversation.id, conversation.unreadCount, minimized, user, setConversations]);

  return (
    <div
      className={cn(
        'relative bg-background border border-border rounded-t-lg shadow-2xl flex flex-col',
        !dragging && 'transition-all',
      )}
      style={{ width, height: minimized ? 48 : height }}
    >
      {!minimized && (
        <div
          onPointerDown={onHandleDown}
          role="separator"
          aria-label="Resize conversation window"
          title="Drag to resize"
          className="absolute -top-1 -left-1 z-10 h-4 w-4 cursor-nwse-resize rounded-full border border-border bg-muted hover:bg-primary/20"
        />
      )}
      <div className="p-2.5 border-b border-border flex items-center justify-between bg-muted/50 rounded-t-lg">
        <button
          onClick={() => setMinimized(m => !m)}
          className="flex items-center gap-2 flex-1 min-w-0 text-left"
        >
          <UserAvatar path={conversation.avatar} name={conversation.name} size="sm" />
          <p className="text-sm font-semibold truncate">{conversation.name}</p>
        </button>
        <div className="flex items-center gap-0.5">
          <button
            onClick={() => { setCustom(null); setExpanded(e => !e); }}
            className="p-1.5 hover:bg-muted rounded"
            aria-label={expanded ? 'Shrink conversation' : 'Expand conversation'}
            title={expanded ? 'Shrink' : 'Expand'}
          >
            {expanded ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          </button>
          <button onClick={() => setMinimized(m => !m)} className="p-1.5 hover:bg-muted rounded" aria-label="Minimize">
            <Minus className="w-4 h-4" />
          </button>
          <button onClick={onClose} className="p-1.5 hover:bg-muted rounded" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
      {!minimized && (
        <div className={cn('flex-1 min-h-0 flex flex-col', dragging && 'select-none')}>
          <ChatBody conversation={conversation} onClose={onClose} compact />
        </div>
      )}
    </div>
  );
};


interface BodyProps { conversation: ChatConversation; onClose: () => void; compact: boolean; }

const ChatBody: React.FC<BodyProps> = ({ conversation, onClose, compact }) => {
  const [input, setInput] = useState('');
  const endRef = useRef<HTMLDivElement>(null);
  const { sendMessage } = useChatContext();
  // Mirrors the server-side block trigger; the DB is the real enforcement.
  const blocked = useIsBlockedPair(conversation.userId);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [conversation.messages]);

  const send = () => {
    if (!input.trim()) return;
    sendMessage(conversation.id, input.trim());
    setInput('');
  };

  return (
    <>
      {!compact && (
        <div className="px-3 py-3 border-b border-border flex items-center gap-2 bg-muted/50 shrink-0 sticky top-0 z-10">
          <button
            onClick={onClose}

            aria-label="Back to messages"
            className="p-2 -ml-1 rounded-full hover:bg-muted active:bg-muted/70 shrink-0"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <UserAvatar path={conversation.avatar} name={conversation.name} size="md" />
          <div className="min-w-0 flex-1">
            <p className="font-semibold truncate">{conversation.name}</p>
            <p className="text-xs text-muted-foreground">Direct message</p>
          </div>
          <button
            onClick={onClose}
            aria-label="Close conversation"
            className="p-2 -mr-1 rounded-full hover:bg-muted active:bg-muted/70 shrink-0"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      )}
      <div className={cn('flex-1 overflow-y-auto space-y-2', compact ? 'p-3' : 'p-4 space-y-3')}>
        {conversation.messages.length === 0 ? (
          <div className="text-center text-muted-foreground py-8">
            <MessageCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No messages yet</p>
          </div>
        ) : (
          (() => {
            // Facebook-style: show receipt only under the sender's most-recent message.
            let lastMineIdx = -1;
            for (let i = conversation.messages.length - 1; i >= 0; i--) {
              if (conversation.messages[i].isMe) { lastMineIdx = i; break; }
            }
            return conversation.messages.map((m, i) => (
              <Bubble key={m.id} message={m} showReceipt={i === lastMineIdx} />
            ));
          })()
        )}
        <div ref={endRef} />
      </div>
      <div className={cn('w-full max-w-full min-w-0 border-t border-border bg-muted/20', compact ? 'p-2.5' : 'p-3 sm:p-4')}>
        {blocked ? (
          <p className="text-center text-xs text-muted-foreground py-1">
            Messaging is unavailable between these users.
          </p>
        ) : (
        <div className="flex w-full max-w-full min-w-0 items-end gap-2">
          {/* A <textarea> has an intrinsic (cols-based) min-content width of
              ~180px+. As a flex item its default `min-width: auto` refuses to
              shrink below that, so `flex-1` alone could not compress it on a
              390px screen — the row grew past the viewport and pushed Send
              off-screen. `min-w-0` + `w-full` lets it actually shrink. */}
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
            }}
            placeholder="Aa"
            rows={1}
            className="flex-1 w-full min-w-0 max-h-28 resize-none border border-border rounded-2xl px-3 py-2 text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-background"
          />
          <button
            onClick={send}
            disabled={!input.trim()}
            className="shrink-0 flex h-11 w-11 items-center justify-center bg-primary text-primary-foreground rounded-full hover:bg-primary/90 transition-colors disabled:opacity-50"
            aria-label="Send"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
        )}
      </div>
    </>
  );
};

const Bubble: React.FC<{ message: Message; showReceipt?: boolean }> = ({ message, showReceipt }) => {
  const t = new Date(message.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const readT = message.read_at
    ? new Date(message.read_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : null;
  return (
    <div className={cn('flex flex-col', message.isMe ? 'items-end' : 'items-start')}>
      <div className={cn(
        'max-w-[80%] px-3 py-2 rounded-2xl text-sm break-words',
        message.isMe ? 'bg-primary text-primary-foreground' : 'bg-muted',
      )}>
        {message.content}
      </div>
      <span className="text-[10px] text-muted-foreground mt-1">{t}</span>
      {showReceipt && message.isMe && (
        <span className="text-[10px] text-muted-foreground mt-0.5">
          {readT ? `Seen ${readT}` : 'Sent'}
        </span>
      )}
    </div>
  );
};

export default MessengerDock;
